import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import SecureLS from 'secure-ls'
import { login, logout, getUser, setAuthorizationHeader, getUserSelf } from 'src/api/userAPI'
import logoDefault from 'src/assets/icons/sm-scius.svg'

const secureLs = new SecureLS()

const defaultState = {
  sidebarShow: true,
  isLoggedIn: false,
  id: undefined,
  username: undefined,
  email: undefined,
  image: undefined,
  cpf: undefined,
  password: undefined,
  token: undefined,
  completed: undefined,
  role: undefined,
  company: undefined,
  isTokenVerified: false,
  nome: undefined,
  sobrenome: undefined,
  telefone: undefined,
  // Informações do grupo do usuário
  group_id: undefined,
  group_name: undefined,
  group: undefined,
}

const getStateFromStorage = () => {
  const appcAuth = secureLs.get('appc-auth-mkplace')

  let stateInLocalStorage = {
    sidebarShow: true,
    isLoggedIn: false,
    username: undefined,
    email: undefined,
    image: undefined,
    cpf: undefined,
    password: undefined,
    completed: undefined,
    role: undefined,
    company: undefined,
    isTokenVerified: false,
    nome: undefined,
    sobrenome: undefined,
    telefone: undefined,
    // Informações do grupo do usuário
    group_id: undefined,
    group_name: undefined,
    group: undefined,
  }

  if (appcAuth) {
    return appcAuth
  }
  return stateInLocalStorage
}

export const loginHandler = createAsyncThunk(
  '/user/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await login(credentials)

      let userGroup = null
      let groupId = null

      // Os dados do grupo já vêm na resposta do login
      groupId = response.data.groupId
      userGroup = {
        id: response.data.groupId,
        name: response.data.group,
      }

      const authState = {
        sidebarShow: true,
        id: response.data.id,
        username: response.data?.username,
        firstname: response.data?.firstname,
        lastname: response.data?.lastname,
        nome: response.data?.nome,
        sobrenome: response.data?.sobrenome,
        image: response.data?.image,
        token: response.data.token,
        phone: response.data?.phone,
        telefone: response.data?.telefone || response.data?.phone,
        cpf: response.data?.cpf,
        email: response.data.email,
        admin: response.data.admin,
        completed: response.data.completed,
        role: response.data.role,
        company: response.data.company,
        companyLogo: response.data.companyLogo,
        jwtToken: response.data.jwt,
        permissions: response.data.permissions,
        // Informações do grupo do usuário (vêm direto do login)
        group_id: groupId,
        group_name: userGroup?.name,
        group: userGroup,
        ...credentials,
      }
      return authState
    } catch (error) {
      return rejectWithValue(error?.response?.data || { message: 'Falha ao realizar login' })
    }
  },
)

export const logoutSuccess = createAsyncThunk('user/logout', async (state) => {
  try {
    await logout()
  } catch (err) {
    console.log('backend not ready with logout')
  }
  return defaultState
})

export const verifyTokenHandler = createAsyncThunk('user/verifyToken', async (_, { getState }) => {
  try {
    const currentState = getState().authReducer.user
    const authData = secureLs.get('appc-auth-mkplace')

    // Configurar headers de autorização
    if (authData) {
      setAuthorizationHeader(authData)
    }

    console.log('🔍 Dados atuais do usuário no verifyToken:', currentState)
    console.log('🔍 currentState.group_id:', currentState.group_id)
    console.log('🔍 currentState.group_name:', currentState.group_name)
    console.log('🔍 currentState.group:', currentState.group)

    // Se não tiver nome e sobrenome, buscar dados completos do usuário
    let userData = currentState
    if (!currentState.nome || !currentState.sobrenome) {
      try {
        const userResponse = await getUserSelf()
        userData = {
          ...currentState,
          firstname: userResponse.data.firstname || currentState.firstname,
          lastname: userResponse.data.lastname || currentState.lastname,
          nome:
            userResponse.data.nome ||
            userResponse.data.firstname ||
            currentState.nome ||
            currentState.firstname,
          sobrenome:
            userResponse.data.sobrenome ||
            userResponse.data.lastname ||
            currentState.sobrenome ||
            currentState.lastname,
          image: userResponse.data.image || currentState.image,
          phone: userResponse.data.phone || currentState.phone,
          telefone:
            userResponse.data.telefone ||
            userResponse.data.phone ||
            currentState.telefone ||
            currentState.phone,
          cpf: userResponse.data.cpf || currentState.cpf,
        }
        console.log('🔍 Dados completos do usuário buscados:', userData)
      } catch (userError) {
        console.error('Erro ao buscar dados completos do usuário no verifyToken:', userError)
        // Se falhar, usar dados já salvos
        userData = currentState
      }
    }

    return {
      isValid: true,
      user: {
        ...userData,
        isLoggedIn: true,
        isTokenVerified: true,
      },
    }
  } catch (error) {
    //limpar o storage
    const secureLs = new SecureLS()
    secureLs.remove('appc-auth-mkplace')
    throw error
  }
})

export const authSlice = createSlice({
  name: 'auth',
  initialState: { user: { ...getStateFromStorage() } },
  reducers: {
    updateSuccess: (
      state,
      { firstname, lastname, city, internalState, gender, birthdate, image },
    ) => {
      state.user = {
        ...state.user,
        firstname,
        lastname,
        nome: firstname,
        sobrenome: lastname,
        city,
        state: internalState,
        gender,
        birthdate,
        image,
        completed: true,
      }
    },
    updateUserData: (state, action) => {
      state.user = {
        ...state.user,
        ...action.payload,
      }
    },
    updateProfilePicture: (state, image) => {
      state.user = {
        ...state.user,
        image: image.payload,
      }
    },
    updateCompanyLogo: (state, image) => {
      console.log(image.payload)
      state.user = {
        ...state.user,
        companyLogo: image.payload.File,
      }
    },
    updateShowSideBar: (state, show) => {
      state.user = {
        ...state.user,
        sidebarShow: show.payload,
      }
    },
    updatePlanSuccess: (state, { plan }) => {
      state.user = {
        ...state.user,
        plan,
      }
    },
    updateCompanySuccess: (state, company) => {
      state.user = {
        ...state.user,
        company: company.payload.id,
        companyLogo: company.payload.image || logoDefault,
      }
    },
    setTokenVerified: (state, action) => {
      state.user.isTokenVerified = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loginHandler.fulfilled, (state, action) => {
      state.user = { ...action.payload, isLoggedIn: true, isTokenVerified: true }
    })
    builder.addCase(logoutSuccess.fulfilled, (state, action) => {
      state.user = { ...action.payload }
    })
    builder.addCase(verifyTokenHandler.fulfilled, (state, action) => {
      state.user = {
        ...state.user,
        ...action.payload.user,
        company: action.payload.company || state.user.company,
        companyLogo: action.payload.companyLogo || state.user.companyLogo,
        role: action.payload.role || state.user.role,
        isLoggedIn: true,
        isTokenVerified: true,
      }
    })
    builder.addCase(verifyTokenHandler.rejected, (state, action) => {
      state.user = { ...defaultState }
    })
  },
})

export const {
  updateSuccess,
  updateUserData,
  updateShowSideBar,
  updatePlanSuccess,
  updateCompanySuccess,
  updateProfilePicture,
  updateCompanyLogo,
  setTokenVerified,
} = authSlice.actions

export default authSlice.reducer
